import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {Debouncer, HTMLUtil, Time, UIUtil, Util} from 'yougile-util'
import {DataObjectId, Hub} from 'yougile-model'
import {ClientUtilities} from '@client/client-utilities'
import {Sync} from '@client/sync'
import {SyncEvents} from '@client/sync-events'
import {FileChooser} from '@common/file-chooser'
import {FilePreview} from '@common/file-preview'
import {FileUploadProgress} from '@components/file-upload-progress'
import {MessageBox} from '@components/message-box'
import {createUseStore} from '@containers/hooks'
import {ChatUtils} from '@extensions/chat-utils'
import {Feedback} from '@extensions/feedback'
import {globals} from '@extensions/globals-base'
import {News} from '@extensions/news'

interface FeedbackType {
  attachments: File[]
  topic: string
  text: string
}

interface State {
  feedback: FeedbackType

  isEditor: boolean
  showLimit: number
  openTasks: Hub[]
  openTasksLastTimestamp: Map<DataObjectId, number>
  closedTasks: Hub[]
  closedTasksLastTimestamp: Map<DataObjectId, number>

  updateTasksHandler?: number
  expandedChatHubId?: DataObjectId
}

export class FeedbackPageStore {
  state: State

  constructor() {
    const feedback: FeedbackType = {
      topic: '',
      text: '',
      attachments: []
    }

    this.state = {
      isEditor: Sync.isNewsEditor,
      showLimit: 10,
      feedback,
      openTasks: [],
      openTasksLastTimestamp: new Map(),
      closedTasks: [],
      closedTasksLastTimestamp: new Map()
    }

    makeAutoObservable(
      this,
      {
        state: observable.shallow
      },
      {autoBind: true}
    )
  }

  private updateTasks(): void {
    this.state.openTasks = Feedback.openTickets()
    this.state.openTasksLastTimestamp = new Map(
      this.state.openTasks.map(x => {
        const ts = x.chat.lastMessage({includeSystem: false})?.timestamp
        return [x.id, ts ?? 0]
      })
    )
    this.state.closedTasks = Feedback.closedTickets()
    this.state.closedTasksLastTimestamp = new Map(
      this.state.closedTasks.map(x => {
        const ts = x.chat.lastMessage({includeSystem: false})?.timestamp
        return [x.id, ts ?? 0]
      })
    )
    this.setBoardColumnMinHeight()
  }

  syncOnNew(): void {
    setTimeout(this.updateTasks)

    this.state.updateTasksHandler = SyncEvents.addListener(
      Sync.profileId,
      Feedback.column.id,
      this.updateTasks
    )
  }

  dispose(): void {
    this.state.updateTasksHandler &&
      SyncEvents.removeListener(this.state.updateTasksHandler)
  }

  updateTopic(topic: string): void {
    const cleanedTopic = Util.removeLeadingString(topic, '.')
    this.state.feedback = {...this.state.feedback, topic: cleanedTopic}
  }

  updateText(text: string): void {
    this.state.feedback = {...this.state.feedback, text}
  }

  deleteAttachment(index: number): void {
    this.state.feedback.attachments.splice(index, 1)
    this.state.feedback = {...this.state.feedback}
  }

  newsAdd(): void {
    // eslint-disable-next-line no-alert
    const title = prompt('Title?')
    if (!title) {
      return
    }
    // eslint-disable-next-line no-alert
    const dateStr = prompt(
      'Date?',
      Time.getParts(Time.now()).slice(0, 3).join('-')
    )
    if (!dateStr) {
      return
    }
    // eslint-disable-next-line no-alert
    const type = prompt('Type?', 'Update')
    if (!type) {
      return
    }
    // eslint-disable-next-line no-alert
    const lang = prompt('Language (ru or en)?', 'ru')
    if (!lang) {
      return
    }
    News.add({title, dateStr, type, lang})
  }

  newsGetImageUrl(task: Hub): string {
    return (
      (
        (task.description.split('<img ')[1] || '').split('src="')[1] || ''
      ).split('"')[0] || ''
    )
  }

  newsGetDescription(task: Hub): string {
    return Util.limitStr(
      HTMLUtil.extractText(FilePreview.encodedToFilenames(task.description)),
      150
    )
  }

  newsShowMore(): void {
    this.state.showLimit += 10
  }

  feedbackSubmitNew(): void {
    const {text, attachments} = this.state.feedback
    const topic = Util.removeLeadingString(this.state.feedback.topic, '.')
    if (!topic) {
      MessageBox.warn(Lang.msg.feedback.noTopicWarn)
      return
    }
    if (!text) {
      MessageBox.warn(Lang.msg.feedback.noTextWarn)
      return
    }

    const len = attachments.length
    if (len > 0) {
      const messages: string[] = []
      let count = 0
      this.state.feedback.attachments = []
      attachments.forEach((f: File, ix: number) => {
        const id = Util.UUID()
        FileUploadProgress.addFile(id, f)
        void ClientUtilities.saveFile(
          {file: f},
          (url: string) => {
            FileUploadProgress.removeFile(id)
            if (!url) {
              return
            }
            messages[ix] = FilePreview.encodeRelativeUrl(url)
            count++
            if (count >= len) {
              const finalText = text + ' \n' + messages.join(' ')
              Feedback.createTicket(topic, finalText)
              MessageBox.show(Lang.msg.feedback.ticketAddSuccess, 'success')
            }
          },
          (progress: number) => {
            FileUploadProgress.updateProgress(id, progress)
          }
        )
      })
    } else {
      Feedback.createTicket(topic, text)
      MessageBox.show(Lang.msg.feedback.ticketAddSuccess, 'success')
    }

    this.state.feedback = {attachments: [], topic: '', text: ''}
  }

  async feedbackAttach(e: React.MouseEvent): Promise<void> {
    await FileChooser.chooseFile(e, true, file => {
      this.state.feedback.attachments.push(file)
    })
    this.state.feedback = {...this.state.feedback}
  }

  initScrollbar(initializer: {update: () => void}): void {
    setTimeout(() => initializer.update())
  }

  updateExpandedChatHubId(): void {
    if (!ChatUtils.isChatExpanded() || !ChatUtils.getOpenedChatHub()) {
      this.state.expandedChatHubId = undefined
      return
    }
    this.state.expandedChatHubId = ChatUtils.getOpenedChatHub()?.id
  }

  openChat(task: Hub, event: React.MouseEvent): void {
    !UIUtil.isConsumed(event) && ChatUtils.toggleChat(task)
    setTimeout(() => this.updateExpandedChatHubId())
  }

  lastMessageText(task: Hub): string {
    const msg = task.chat.lastMessage({includeSystem: false})
    if (!msg) {
      return ''
    }
    const text = FilePreview.encodedToFilenames(msg.text)
    return Util.limitStr(text, 80)
  }

  newMessagesCount(task: Hub): number {
    return task.chat.newMessagesFor(globals.currentUserId, 0, false)
  }

  setBoardColumnMinHeight(): void {
    const up = (): void => {
      const minH = document.documentElement.clientHeight - 130
      const el = document.querySelector<HTMLElement>('.task-list')
      if (el) {
        el.style.height = minH + 'px'
      }
    }
    setTimeout(() => up())
    const wnd = window as any
    const resizer = new Debouncer(500)
    if (!wnd.tlrel) {
      wnd.tlrel = true
      window.addEventListener('resize', () => resizer.runButNotOften(up))
    }
  }
}

export const [FeedbackPageContext, useFeedbackPageStore] =
  createUseStore(FeedbackPageStore)
